// Light-emitter post-pass. Each material that declares `light_emission`
// becomes a soft radial glow centered on its cell. The pass runs in JS,
// after the per-cell shader has filled the RGBA buffer, by additively
// blending a precomputed kernel over the existing data. CPU-side keeps
// it portable and the kernel is small enough that a 256x256 grid with a
// few dozen emitters stays well inside one frame's budget.

import type { LightEmission, MaterialDef } from "../sim/types";

interface LightSource {
  /** Pre-multiplied glow color: R/G/B already scaled by intensity. */
  r: number;
  g: number;
  b: number;
  /** Glow radius in cells. */
  radius: number;
  /** Squared radius cached so the inner loop can compare without a sqrt. */
  radius2: number;
  /** 1 / radius cached for the falloff curve. */
  invRadius: number;
}

/** Indexed by material id; `null` = no light. */
export type LightTable = Array<LightSource | null>;

/** Build the per-material light table once from the chemistry palette. */
export function buildLightTable(palette: MaterialDef[]): LightTable {
  return palette.map((m, idx) => (idx === 0 ? null : extract(m.light_emission)));
}

function extract(le: LightEmission | undefined): LightSource | null {
  if (!le) return null;
  // Loosely typed because the YAML parser returns plain objects, not
  // strict typed instances.
  const obj = le as unknown as {
    color?: unknown;
    radius?: unknown;
    intensity?: unknown;
  };
  if (!Array.isArray(obj.color) || obj.color.length < 3) return null;
  const radius = typeof obj.radius === "number" ? obj.radius : 0;
  if (radius <= 0) return null;
  const intensity = typeof obj.intensity === "number" ? obj.intensity : 1.0;
  const [r, g, b] = obj.color as [number, number, number];
  return {
    r: r * intensity,
    g: g * intensity,
    b: b * intensity,
    radius,
    radius2: radius * radius,
    invRadius: 1 / radius,
  };
}

/**
 * Splat all light emitters onto the existing RGBA buffer.
 *
 * For every emitter cell, walk its bounding square; for each cell inside
 * the radius, add a smoothly-falling weighted color to the existing
 * pixel. The falloff curve is a quintic smootherstep
 * (`t³ × (t × (6t − 15) + 10)` with `t = 1 − d/R`), which is
 * C²-continuous at both ends. Compared to the old quadratic
 * `(1 − d/R)²`, the centre region is brighter for longer (mid-radius
 * stays at ~50% rather than 25%) and the outer rim approaches zero
 * with a flatter tangent — together that reads as a real-light bloom
 * rather than a sharp halo with a saturated white core ringed by
 * an abrupt warm fringe.
 *
 * The pass is read-modify-write on `data`; cells already coloured by the
 * material shader keep their hue but get tinted toward the light colour.
 */
export function applyLightPass(
  data: Uint8Array,
  materialIds: Uint16Array,
  lights: LightTable,
  gridWidth: number,
  gridHeight: number,
): void {
  const len = materialIds.length;
  for (let i = 0; i < len; i++) {
    const light = lights[materialIds[i]];
    if (!light) continue;
    const cx = i % gridWidth;
    const cy = (i / gridWidth) | 0;
    splat(data, gridWidth, gridHeight, cx, cy, light);
  }
}

function splat(
  data: Uint8Array,
  w: number,
  h: number,
  cx: number,
  cy: number,
  light: LightSource,
): void {
  const r = light.radius;
  const r2 = light.radius2;
  const yStart = Math.max(0, cy - r);
  const yEnd = Math.min(h - 1, cy + r);
  const xStart = Math.max(0, cx - r);
  const xEnd = Math.min(w - 1, cx + r);
  for (let y = yStart; y <= yEnd; y++) {
    const dy = y - cy;
    const dy2 = dy * dy;
    for (let x = xStart; x <= xEnd; x++) {
      const dx = x - cx;
      const d2 = dx * dx + dy2;
      if (d2 > r2) continue;
      // Smootherstep falloff: t = 1 - d/R, weight = t^3 * (t*(6t-15)+10).
      // Zero-derivative at both t=0 and t=1 so the bloom blends into
      // the surrounding scene without a visible rim.
      const d = Math.sqrt(d2);
      const t = 1 - d * light.invRadius;
      const s = t * t * t * (t * (t * 6 - 15) + 10);
      const j = (y * w + x) * 4;
      data[j] = clampAdd(data[j], light.r * s);
      data[j + 1] = clampAdd(data[j + 1], light.g * s);
      data[j + 2] = clampAdd(data[j + 2], light.b * s);
      // Force alpha so empty cells light up too (otherwise a glow over
      // empty space would be invisible).
      if (data[j + 3] < 255) data[j + 3] = 255;
    }
  }
}

function clampAdd(a: number, b: number): number {
  const s = a + b;
  return s > 255 ? 255 : s | 0;
}
